import React, { memo } from 'react'
import { useUpdateRate } from 'apps/marketplace/apiTypes/requestForProposalLaneCarrierRates'
import { IRFPLaneRate } from 'apps/marketplace/utils/api/rfpLaneRates'
import { extractRfpLaneFromRate } from 'apps/marketplace/utils/helpers/rfpLaneRate'
import { useRFPLaneEditRateForm } from 'apps/marketplace/utils/hooks/useRFPLaneRateForm'
import { FullPageOverlay } from 'transfix-ui'
import { getError } from 'utils/parseError'
import { RateForm } from './rate-form/RateForm'

interface IEditRateModalProps {
  rfpLaneRate: IRFPLaneRate
  closeAction: (type?: string) => void
  awardedLaneHeader?: React.ReactElement
}

export const EditRateModal = memo(
  ({ awardedLaneHeader, closeAction, rfpLaneRate }: IEditRateModalProps) => {
    const onSuccess = () => closeAction('success')
    const cancelAction = () => closeAction()
    const {
      control,
      errors: formErrors,
      formState,
      handleSubmit,
      setValue,
    } = useRFPLaneEditRateForm({
      rfpLaneRate,
    })
    const rfpLane = extractRfpLaneFromRate(rfpLaneRate)
    const { error, loading, mutate: updateLaneRate } = useUpdateRate({ id: String(rfpLaneRate.id) })

    const stepEditConfig = [
      {
        title: 'Edit Sourced Rate',
        primaryActionConfig: {
          onClick: handleSubmit(data => {
            updateLaneRate({
              rfpLaneCarrierRate: {
                carrierApplicationId: data.carrierApplicationId,
                maxCapacity: data.maxCapacity,
                minCapacity: data.minCapacity,
                canDoWeekends: data.canDoWeekends,
                confirmed: data.confirmed,
                rateAttributes: {
                  id: String(rfpLaneRate.id),
                  amount: data.rateAttributes.amount,
                  rateTypeKey: data.rateAttributes.rateTypeKey,
                },
              },
            }).then(() => {
              onSuccess()
            })
          }),
          buttonText: 'Save Changes',
          disabled: loading || !formState.isValid,
        },
        secondaryActionConfig: {
          onClick: cancelAction,
          buttonText: 'Cancel',
          disabled: loading,
        },
      },
    ]

    const step = [
      <RateForm
        key='step-1'
        selectedRFPLane={rfpLane}
        control={control}
        setValue={setValue}
        defaultCarrier={{
          ...rfpLaneRate.carrierApplication,
          id: String(rfpLaneRate.carrierApplication?.id),
        }}
        capacityIsRange={!!rfpLaneRate.minCapacity}
        formErrors={formErrors}
        loading={loading}
        awardedLaneHeader={awardedLaneHeader}
      />,
    ]

    return (
      <FullPageOverlay
        open={!!rfpLaneRate}
        onClose={closeAction}
        stepConfig={stepEditConfig}
        onPressBackButton={closeAction}
        currentStep={0}
        errors={error ? [getError(error)] : []}>
        {step}
      </FullPageOverlay>
    )
  }
)
